import { FC, useState } from 'react';
import {
    Box,
    CircularProgress,
    Alert,
    Paper,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    type SelectChangeEvent,
} from '@mui/material';
import { useRetention } from '../../../hooks/analytics/useUserActivity';
import { KpiCard } from '../common/KpiCard';
import { StatsLineChart } from '../common/StatsLineChart';

interface Props {
    startDate: string;
    endDate: string;
}

const SEGMENTS = [
    { value: 'ALL', label: '전체' },
    { value: 'SUBSCRIBED', label: '구독' },
    { value: 'FREE', label: '무료' },
] as const;

const getCellBg = (val: number | null | undefined) =>
    val == null
        ? '#f5f5f5'
        : val > 60
          ? '#e8f5e9'
          : val > 40
            ? '#fff8e1'
            : val > 20
              ? '#fff3e0'
              : '#ffebee';

const RetentionSection: FC<Props> = ({ startDate, endDate }) => {
    const [segment, setSegment] = useState('ALL');
    const { data, isLoading, isError, error } = useRetention(startDate, endDate, segment);

    const handleSegmentChange = (e: SelectChangeEvent) => {
        setSegment(e.target.value);
    };

    const curveData = (data?.curve ?? []).map((p) => ({
        ...p,
        dayLabel: `D${p.dayN}`,
    }));

    const kpi = data?.kpi;
    const cohortTable = data?.cohortTable ?? [];

    const RETENTION_DAYS = [
        { key: 'd1' as const, label: 'D1' },
        { key: 'd3' as const, label: 'D3' },
        { key: 'd7' as const, label: 'D7' },
        { key: 'd14' as const, label: 'D14' },
        { key: 'd30' as const, label: 'D30' },
    ];

    const COHORT_COLUMNS = ['d1', 'd3', 'd7', 'd14', 'd30', 'd60', 'd90'] as const;

    return (
        <Paper sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">리텐션 분석</Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>세그먼트</InputLabel>
                    <Select value={segment} label="세그먼트" onChange={handleSegmentChange}>
                        {SEGMENTS.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {isError && <Alert severity="error">{(error as Error)?.message ?? '데이터를 불러오지 못했습니다.'}</Alert>}

            {data && (
                <>
                    {/* Retention Curve */}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        잔존율 곡선
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <StatsLineChart
                            data={curveData}
                            series={[{ dataKey: 'retentionRate', name: '잔존율', color: '#1976d2' }]}
                            xAxisKey="dayLabel"
                        />
                    </Box>

                    {/* Retention KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {RETENTION_DAYS.map(({ key, label }) => (
                            <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={key}>
                                <KpiCard
                                    title={`${label} 잔존율`}
                                    value={`${(kpi?.[key] ?? 0).toFixed(1)}%`}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Cohort Table */}
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        코호트 테이블
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>가입 주차</TableCell>
                                    <TableCell align="right">가입자</TableCell>
                                    {COHORT_COLUMNS.map((col) => (
                                        <TableCell key={col} align="right">
                                            {col.toUpperCase()}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cohortTable.map((row) => (
                                    <TableRow key={row.cohortWeek}>
                                        <TableCell>{row.cohortWeek}</TableCell>
                                        <TableCell align="right">{row.signupCount.toLocaleString()}</TableCell>
                                        {COHORT_COLUMNS.map((col) => {
                                            const val = row[col];
                                            return (
                                                <TableCell
                                                    key={col}
                                                    align="right"
                                                    sx={{ backgroundColor: getCellBg(val) }}
                                                >
                                                    {val != null ? `${val.toFixed(1)}%` : '-'}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Paper>
    );
};

export default RetentionSection;
